import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ContactField, Cryptocoin, CryptoWallet, Price } from "@models";
import { CryptocoinService } from "@main-services/currency";
import { RecipientInfoModel } from "@modules/main/components/step-summary/sending-modal/entities/recipient-info.model";
import { SendTransaction } from "@models/transaction";
import { StellarSendType } from "@helpers/types";
import { catchError, filter, tap } from "rxjs/operators";
import { NEVER, Observable } from "rxjs";
import { Fee } from "@modules/main/components/step-summary/sending-modal/entities/fee.model";
import { SendingModalRemoteService } from "@modules/main/components/step-summary/sending-modal/services/sending-modal-remote.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { Helper } from "@helpers/functions";

@Component({
	selector: "sending-recipient",
	templateUrl: "./sending-recipient.component.html",
	providers: [SendingModalRemoteService],
	standalone: false
})
export class SendingRecipientComponent implements OnInit {
	@Input() wallet: CryptoWallet;
	@Output() onSelectRecipient: EventEmitter<RecipientInfoModel> = new EventEmitter<RecipientInfoModel>();
	public selectedAsset: Cryptocoin;
	private validationRunning = false;
	public recipientAddress: string = "";
	public searchString: string = "";
	public recipientError = "";
	public destinationTag = "";
	public destinationTagError = "";
	private price: Price;
	private cf = new ContactField();

	constructor(
		private cryptoCoinService: CryptocoinService,
		private infoService: InfoService,
		private translationService: TranslationService,
		private sendingModalRemoteService: SendingModalRemoteService
	) {}

	ngOnInit(): void {
		this.selectedAsset = this.cryptoCoinService.getItemById(this.wallet.getCurrencyId());
	}

	public recipientAddressAdded(destinationTag: string): void {
		this.selectRecipient(destinationTag);
	}

	public onKeyPress(value: string): void {
		this.searchString = value;
	}

	public selectRecipient(destinationTag?: string): void {
		if (destinationTag) {
			this.destinationTag = destinationTag;
		}
		this.recipientAddress = this.searchString;
		if (this.recipientAddress.length === 0) {
			this.recipientError = "receiver_must_not_be_empty_send_modal";
			return;
		}

		if (this.selectedAsset.getSymbol(true) === "xem") {
			if (this.destinationTag.trim().length > 128) {
				this.destinationTagError = "tag_too_long";
				return;
			}
		}

		const transaction = new SendTransaction();
		transaction.setWallet(this.wallet);
		transaction.setRecipient(this.recipientAddress);
		transaction.setDestinationTag(this.destinationTag);

		this.cf.setValue(this.recipientAddress);
		this.cf.setDestinationTag(transaction.getDestinationTag());
		this.cf.dt_type = transaction.getDtType() as StellarSendType;
		this.validateContact(transaction)
			.pipe(filter((f) => !!f))
			.subscribe((fee) => {
				if (this.checkForErrors(fee.getAllowSending())) {
					return;
				}
			});
	}

	public selectContact(contactField: ContactField): void {
		this.cf = contactField;
		const t = new SendTransaction();
		t.setRecipient(contactField.getValue());
		this.recipientAddress = contactField.getValue();
		this.destinationTag = contactField.getDestinationTag();
		t.setDestinationTag(contactField.getDestinationTag());
		t.setDtType(contactField.dt_type);

		this.validateContact(t)
			.pipe(filter((f) => !!f))
			.subscribe((fee) => {
				if (this.checkForErrors(fee.getAllowSending())) {
					return;
				}
			});
	}

	public checkForErrors(allowedSending: string): boolean {
		if (allowedSending === "both") {
			if (!this.selectedAsset.isTransferActive()) {
				if (!this.selectedAsset.isDepositActive()) {
					this.recipientError = "cannot_send_to_bitpanda_address_right_now";
					return true;
				}
				if (!this.hasEnoughFundsForExternal()) {
					this.recipientError = "cannot_send_external_because_wallet_balance";
					return true;
				}
			}
		} else if (allowedSending === "internal" && !this.selectedAsset.isTransferActive()) {
			this.recipientError = "cannot_send_to_email_right_now";
			return true;
		}
		return false;
	}

	public hasEnoughFundsForExternal(): boolean {
		return (
			+this.price.getMaxAmountExternal().tradableAmount() >= +this.price.getMinAmountExternal().tradableAmount()
		);
	}

	private validateContact(transaction: SendTransaction): Observable<Fee> {
		if (this.validationRunning) {
			this.infoService.setWarning(this.translationService.translate("please_wait_ongoing_call_send_check"));
			return NEVER;
		}

		this.validationRunning = true;
		return this.sendingModalRemoteService.getFeeForTransaction(transaction, this.wallet.getId()).pipe(
			tap((fee: Fee) => this.fetchLimits(fee)),
			tap(() => (this.validationRunning = false)),
			catchError((error) => this.handleError(error, transaction))
		);
	}

	private handleError(error, transaction): Observable<never> {
		Helper.handleError(error, transaction, {
			forceToast: true,
			callback: () => {
				this.recipientError = "validation_error_possible_external";
			}
		});
		this.validationRunning = false;
		return NEVER;
	}

	private fetchLimits(fee: Fee): void {
		let sendType: string = fee.getAllowSending() === "both" ? "internal" : fee.getAllowSending();
		this.sendingModalRemoteService.fetchPriceLimits(sendType, this.wallet.getId()).subscribe({
			next: (price) => {
				this.price = price;
				this.cf.setDestinationTag(this.destinationTag);
				this.onSelectRecipient.emit({
					price: price,
					sendType: sendType,
					fee: fee,
					recipientAddress: this.recipientAddress,
					destinationTag: this.destinationTag,
					contactField: this.cf
				});
				this.validationRunning = false;
			},
			error: (e) => Helper.handleError(e)
		});
	}

	public onMultiplePossibleRecipients(contactName: string): void {
		this.recipientAddress = contactName;
	}

	public selectNewAddress(): void {
		this.selectRecipient();
	}
}
